import { useEffect, useMemo, useState } from 'react'
import Form from 'react-bootstrap/Form'
import { useDispatch, useSelector } from 'react-redux'
import { updateTopHeader, uplodateLoading } from '../../../redux/features/common/commonSlice'
import ApiCaller from '../../constants/ApiCaller'
import urls from '../../constants/apiurl'
import { showDangerToast, showToast } from '../../constants/toaster'
import NavBar from '../../structure/NavBar'
import { NavBarTop } from '../../structure/NavBarTop'
import InsufficientPermission from '../../custom/InSufficientPermission'

const VERSION_REGEX = /^\d+(\.\d+){0,3}$/

const DEFAULT_FORM = {
  androidVersion: '',
  iosVersion: '',
  androidForceUpdate: false,
  iosForceUpdate: false,
  underMaintenance: false,
  message: ''
}

export const AppVersionConfigPage = () => {
  const [formData, setFormData] = useState(DEFAULT_FORM)
  const [errors, setErrors] = useState({})
  const [lastLoadedAt, setLastLoadedAt] = useState('')
  const dispatch = useDispatch()
  const userData = useSelector((state) => state?.userData)
  const userPermissions = useSelector((state) => state?.permission)

  const permissionState = useMemo(() => {
    let viewPermission = true
    let editPermission = true
    if (userPermissions?.modules?.length > 0) {
      const modulePermissions = userPermissions.modules.find((element) => element.moduleId === '19')?.permissions || []
      viewPermission = modulePermissions.find((element) => element.name === 'VIEW')?.isEnable ?? true
      editPermission = modulePermissions.find((element) => element.name === 'EDIT')?.isEnable ?? true
    }
    return { viewPermission, editPermission }
  }, [userPermissions])

  const requiresMessage = formData.underMaintenance || formData.androidForceUpdate || formData.iosForceUpdate

  const normalizeIncoming = (data) => ({
    androidVersion: (data?.androidVersion || '').trim(),
    iosVersion: (data?.iosVersion || '').trim(),
    androidForceUpdate: Boolean(data?.androidForceUpdate),
    iosForceUpdate: Boolean(data?.iosForceUpdate),
    underMaintenance: Boolean(data?.underMaintenance),
    message: (data?.message || '').trim()
  })

  const validate = (data) => {
    const validationErrors = {}
    if (!data.androidVersion || !VERSION_REGEX.test(data.androidVersion)) {
      validationErrors.androidVersion = 'Android version must be dotted numeric, e.g. 3.40.0'
    }
    if (!data.iosVersion || !VERSION_REGEX.test(data.iosVersion)) {
      validationErrors.iosVersion = 'iOS version must be dotted numeric, e.g. 2.1.0'
    }
    const needsMessage = data.underMaintenance || data.androidForceUpdate || data.iosForceUpdate
    if (needsMessage && !data.message) {
      validationErrors.message = 'Message is required when maintenance/force-update is enabled.'
    }
    return validationErrors
  }

  const getAppVersionConfig = async () => {
    if (!userData?.access_token) {
      return
    }
    try {
      dispatch(uplodateLoading(true))
      const res = await ApiCaller.get({}, urls.getAppVersionConfig, userData?.access_token)
      const response = await res.response.json()
      if (response.status == 200) {
        setFormData(normalizeIncoming(response?.data || {}))
        setLastLoadedAt(new Date().toLocaleString())
        setErrors({})
      } else if (response.status == 401) {
        showDangerToast('Session expired or invalid token. Please login again.')
      } else {
        showDangerToast(response.message || 'Failed to load app version config.')
      }
    } catch (error) {
      showDangerToast(error.message)
    } finally {
      dispatch(uplodateLoading(false))
    }
  }

  const saveAppVersionConfig = async () => {
    const payload = normalizeIncoming(formData)
    const validationErrors = validate(payload)
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) {
      return
    }
    try {
      dispatch(uplodateLoading(true))
      const res = await ApiCaller.post(payload, urls.setAppVersionConfig, userData?.access_token)
      const response = await res.response.json()
      if (response.status == 200) {
        setFormData(normalizeIncoming(response?.data || payload))
        showToast(response?.message || 'App version config saved successfully')
      } else {
        showDangerToast(response.message || 'Failed to save app version config.')
      }
    } catch (error) {
      showDangerToast(error.message)
    } finally {
      dispatch(uplodateLoading(false))
    }
  }

  const onFieldChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }))
    }
  }

  useEffect(() => {
    dispatch(updateTopHeader('App Version Config'))
  }, [dispatch])

  useEffect(() => {
    if (permissionState.viewPermission && userData?.access_token) {
      getAppVersionConfig()
    }
  }, [permissionState.viewPermission, userData?.access_token])

  return (
    <>
      <NavBar />
      <div className='main-content'>
        <NavBarTop />
        <div className='header'>
          <div className='logo'>
            <div className='header bg-gradient-primary pb-8 pt-5 pt-md-8'></div>
          </div>
        </div>
        {permissionState.viewPermission ? (
          <div className='container-fluid mt--7'>
            <div className='row'>
              <div className='col'>
                <div className='card shadow'>
                  <div className='card-header border-0'>
                    <div className='row align-items-center'>
                      <div className='col'>
                        <h3 className='mb-0'>App Version Config</h3>
                      </div>
                      <div className='col text-right'>
                        <button
                          className='btn btn-sm btn-outline-primary mr-2'
                          onClick={getAppVersionConfig}
                        >
                          Refresh
                        </button>
                        <button
                          className='btn btn-sm btn-primary text-white'
                          onClick={saveAppVersionConfig}
                          disabled={!permissionState.editPermission}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className='card-body'>
                    <div className='row mb-2'>
                      <div className='col-md-12'>
                        <small className='text-muted'>
                          {lastLoadedAt ? `Last synced: ${lastLoadedAt}` : 'Click Refresh to sync current DB values.'}
                        </small>
                      </div>
                    </div>
                    <div className='row'>
                      <div className='col-md-6'>
                        <Form.Group className='mb-3 p-3 border rounded'>
                          <Form.Label>Android Version</Form.Label>
                          <Form.Control
                            type='text'
                            value={formData.androidVersion}
                            disabled={!permissionState.editPermission}
                            onChange={(e) => onFieldChange('androidVersion', e.target.value)}
                            placeholder='e.g. 3.40.0'
                          />
                          {errors.androidVersion ? <small className='text-danger'>{errors.androidVersion}</small> : null}
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-3 p-3 border rounded'>
                          <Form.Label>iOS Version</Form.Label>
                          <Form.Control
                            type='text'
                            value={formData.iosVersion}
                            disabled={!permissionState.editPermission}
                            onChange={(e) => onFieldChange('iosVersion', e.target.value)}
                            placeholder='e.g. 2.1.0'
                          />
                          {errors.iosVersion ? <small className='text-danger'>{errors.iosVersion}</small> : null}
                        </Form.Group>
                      </div>
                    </div>
                    <div className='row'>
                      <div className='col-md-4'>
                        <Form.Group className='mb-3 p-3 border rounded h-100'>
                          <Form.Label>Android Force Update</Form.Label>
                          <Form.Select
                            value={String(formData.androidForceUpdate)}
                            disabled={!permissionState.editPermission}
                            onChange={(e) => onFieldChange('androidForceUpdate', e.target.value === 'true')}
                          >
                            <option value='false'>False</option>
                            <option value='true'>True</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                      <div className='col-md-4'>
                        <Form.Group className='mb-3 p-3 border rounded h-100'>
                          <Form.Label>iOS Force Update</Form.Label>
                          <Form.Select
                            value={String(formData.iosForceUpdate)}
                            disabled={!permissionState.editPermission}
                            onChange={(e) => onFieldChange('iosForceUpdate', e.target.value === 'true')}
                          >
                            <option value='false'>False</option>
                            <option value='true'>True</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                      <div className='col-md-4'>
                        <Form.Group className='mb-3 p-3 border rounded h-100'>
                          <Form.Label>Under Maintenance</Form.Label>
                          <Form.Select
                            value={String(formData.underMaintenance)}
                            disabled={!permissionState.editPermission}
                            onChange={(e) => onFieldChange('underMaintenance', e.target.value === 'true')}
                          >
                            <option value='false'>False</option>
                            <option value='true'>True</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </div>
                    <div className='row'>
                      <div className='col-md-12'>
                        <Form.Group className='mb-3 p-3 border rounded'>
                          <Form.Label>
                            Message {requiresMessage ? <span className='text-danger'>*</span> : null}
                          </Form.Label>
                          <Form.Control
                            as='textarea'
                            rows={3}
                            value={formData.message}
                            disabled={!permissionState.editPermission}
                            onChange={(e) => onFieldChange('message', e.target.value)}
                            placeholder='Shown to users during forced update/maintenance'
                          />
                          {errors.message ? <small className='text-danger'>{errors.message}</small> : null}
                        </Form.Group>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <InsufficientPermission />
        )}
      </div>
    </>
  )
}

